import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Check } from 'lucide-react';
import { parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { cn } from '@/lib/utils';

/** Legend order (Figma left→right). */
const LEGEND = [
  { key: 'active', name: 'Active Users', color: '#3b82f6' },
  { key: 'signups', name: 'New Signups', color: '#a855f7' },
  { key: 'deleted', name: 'Deleted Accounts', color: '#f43f5e' },
] as const;

/** Stack order bottom→top (Figma): Deleted, New Signups, Active. */
const STACK_ORDER = ['deleted', 'signups', 'active'] as const;

type SeriesKey = (typeof STACK_ORDER)[number];

/** Weekday initial (S M T W T F S) for daily buckets — matches Figma. */
function weekdayInitial(iso: string): string {
  try {
    const d = parseISO(iso);
    return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][d.getUTCDay()] ?? '';
  } catch {
    return '';
  }
}

/**
 * Figma User account Overview — title → toggle legend → stacked bars.
 * Date and country follow the page-global filters on Platform Growth Overview.
 */
export function DashboardAccountOverviewChart({
  categories,
  activeUsers,
  newSignups,
  deletedAccounts,
  isLoading,
}: {
  categories: string[];
  activeUsers: number[];
  newSignups: number[];
  deletedAccounts: number[];
  isLoading?: boolean;
}) {
  const isDark = useIsDarkMode();
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    active: true,
    signups: true,
    deleted: true,
  });

  const option = useMemo(() => {
    const ink = {
      muted: tone(CHART_INK.muted, isDark),
      surface: tone(CHART_INK.surface, isDark),
      primary: tone(CHART_INK.primary, isDark),
      grid: tone(CHART_INK.grid, isDark),
    };
    const useWeekdayLabels = categories.length > 0 && categories.length <= 14;
    const dataByKey: Record<SeriesKey, number[]> = {
      active: activeUsers,
      signups: newSignups,
      deleted: deletedAccounts,
    };
    const metaByKey = Object.fromEntries(LEGEND.map((s) => [s.key, s])) as Record<
      SeriesKey,
      (typeof LEGEND)[number]
    >;
    const stacked = STACK_ORDER.filter((key) => visible[key]);
    const topStackKey = stacked.at(-1);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
        axisPointer: { type: 'shadow' },
      },
      grid: { left: 4, right: 4, top: 8, bottom: 4, containLabel: true },
      xAxis: {
        type: 'category',
        data: useWeekdayLabels
          ? categories.map(weekdayInitial)
          : categories.map((c) => {
              try {
                return String(parseISO(c).getUTCDate());
              } catch {
                return c;
              }
            }),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: ink.muted,
          interval: 0,
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        show: false,
        splitLine: { show: false },
      },
      series: stacked.map((key) => {
        const s = metaByKey[key];
        return {
          name: s.name,
          type: 'bar',
          stack: 'account',
          barMaxWidth: 36,
          barCategoryGap: '42%',
          itemStyle: {
            color: s.color,
            borderRadius: key === topStackKey ? ([8, 8, 0, 0] as const) : ([0, 0, 0, 0] as const),
          },
          data: dataByKey[key],
        };
      }),
    };
  }, [activeUsers, categories, deletedAccounts, isDark, newSignups, visible]);

  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="space-y-4 p-5 pb-2">
        <CardTitle className="text-sm font-medium text-foreground">
          User account Overview
        </CardTitle>

        <div className="flex flex-wrap items-center gap-5">
          {LEGEND.map((s) => {
            const on = visible[s.key];
            return (
              <button
                key={s.key}
                type="button"
                className="inline-flex items-center gap-2 text-sm text-foreground"
                aria-pressed={on}
                onClick={() =>
                  setVisible((prev) => ({ ...prev, [s.key]: !prev[s.key] }))
                }
              >
                <span
                  className={cn(
                    'inline-flex size-5 items-center justify-center rounded-full',
                    !on && 'opacity-40',
                  )}
                  style={{ backgroundColor: s.color }}
                  aria-hidden
                >
                  {on ? <Check className="size-3 text-white" strokeWidth={3} /> : null}
                </span>
                {s.name}
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="p-5 pt-2">
        {isLoading ? (
          <div className="flex h-[260px] items-center justify-center text-xs text-muted-foreground">
            Loading account overview…
          </div>
        ) : categories.length === 0 ? (
          <div className="flex h-[260px] items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
            No account activity for this period.
          </div>
        ) : (
          <ReactECharts
            option={option}
            notMerge
            style={{ height: 260, width: '100%' }}
            className={cn(!Object.values(visible).some(Boolean) && 'opacity-40')}
          />
        )}
      </CardContent>
    </Card>
  );
}
